/**
 * StarRating — a small presentational 0-5 star display.
 *
 * Renders five lucide `Star` icons, filling `Math.round(rating)` of them.
 * Mirrors the star styling used by the Reviews surface
 * (`features/reviews/components/ReviewInspector.tsx`) so a review rating
 * looks consistent wherever it appears — here it's reused on collapsed
 * Attention Queue rows for `post_review_reply` approvals.
 */

import type { CSSProperties } from 'react'
import { Star } from 'lucide-react'

export interface StarRatingProps {
  /** Rating from 0-5. Rounded to the nearest whole star. */
  rating: number
  /** Icon size in px. Defaults to 13 to sit comfortably inline on a row. */
  size?: number
}

export function StarRating({ rating, size = 13 }: StarRatingProps) {
  const stars = Math.max(0, Math.min(5, Math.round(rating)))

  return (
    <span style={styles.row} aria-label={`${stars} out of 5 stars`} role="img">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          style={i < stars ? styles.filled : styles.empty}
          fill={i < stars ? 'currentColor' : 'none'}
          strokeWidth={1.5}
        />
      ))}
    </span>
  )
}

const styles: Record<string, CSSProperties> = {
  row: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
  },
  filled: { color: 'var(--warning)' },
  empty: { color: 'var(--border-default)' },
}

export default StarRating
